<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import DateRangeInput from '@/js/components/DateRangeInput.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';
    import SyncJobsTable from '@/js/back-office/components/SyncJobsTable.vue';
    import {
        useQueryGetIntegrationOverview,
        useMutationEnqueueIntegrationManualSync,
        useQueryGetIntegrationSyncJobs,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import { GENESYS_SYSTEM_KEY } from '@/js/consts/integration-systems';

    const GENESYS_DATA_TYPE_CALL_INTERACTIONS = 'CallInteractions';
    const GENESYS_DATA_TYPE_EMAIL_INTERACTIONS = 'EmailInteractions';

    const successDialogStore = useSuccessDialog();

    // Genesys tenants only — derived from the overview data rather than the full tenant list,
    // since a backfill only makes sense for a tenant that already has Genesys configured.
    const { data: overviewData } = useQueryGetIntegrationOverview(GENESYS_SYSTEM_KEY);

    const tenantOptions = computed<Option[]>(() => {
        const byId = new Map<string, string>();
        for (const row of overviewData.value ?? []) {
            byId.set(row.tenantId, row.tenantName);
        }
        return Array.from(byId, ([value, name]) => ({ name, value }));
    });

    const selectedTenantId = ref<string | null>(null);
    const tenantSearch = ref<string | null>(null);
    const dateRange = ref<{ start?: string; end?: string }>({});

    const syncJobsTable = ref<InstanceType<typeof SyncJobsTable> | null>(null);

    const { mutateAsync: syncMutation, isPending } =
        useMutationEnqueueIntegrationManualSync(GENESYS_SYSTEM_KEY);

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };
    const onTenantClear = () => {
        selectedTenantId.value = null;
    };

    const findRow = (dataType: string) =>
        (overviewData.value ?? []).find(
            (row) => row.tenantId === selectedTenantId.value && row.dataType === dataType
        );

    const includeInteractions = ref(false);
    const includeEmailInteractions = ref(false);

    // Default to whichever data types the tenant actually has enabled, so switching tenant
    // doesn't silently carry over a selection that means nothing for the new one.
    watch(selectedTenantId, () => {
        includeInteractions.value = findRow(GENESYS_DATA_TYPE_CALL_INTERACTIONS)?.isEnabled ?? false;
        includeEmailInteractions.value =
            findRow(GENESYS_DATA_TYPE_EMAIL_INTERACTIONS)?.isEnabled ?? false;
    });

    const selectedDataTypes = computed<string[]>(() => {
        const types: string[] = [];
        if (includeInteractions.value) types.push(GENESYS_DATA_TYPE_CALL_INTERACTIONS);
        if (includeEmailInteractions.value) types.push(GENESYS_DATA_TYPE_EMAIL_INTERACTIONS);
        return types;
    });

    const canSubmit = computed(
        () =>
            !!selectedTenantId.value &&
            !!dateRange.value.start &&
            !!dateRange.value.end &&
            dateRange.value.end >= dateRange.value.start &&
            selectedDataTypes.value.length > 0
    );

    const submit = async () => {
        if (!canSubmit.value) return;

        const result = await syncMutation({
            tenantId: selectedTenantId.value!,
            start: `${dateRange.value.start}T00:00:00`,
            end: `${dateRange.value.end}T23:59:59`,
            dataTypes: selectedDataTypes.value,
        });

        successDialogStore.openDialog({
            message: __('back-office-sync-jobs-success', { id: String(result.id) }),
        });
        syncJobsTable.value?.refetch();
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-genesys-tab-sync') }}</h1>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <AutoComplete
                        v-model="tenantSearch"
                        input-id="genesys-sync-data-tenant-select"
                        :label="__('back-office-sync-jobs-select-tenant')"
                        :options="tenantOptions"
                        @select="onTenantSelect"
                        @clear="onTenantClear"
                    />
                    <div class="max-w-xs">
                        <DateRangeInput v-model="dateRange" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-overline">
                            {{ __('back-office-genesys-sync-data-types') }}
                        </label>
                        <CheckboxInput
                            id="genesys-sync-data-type-interactions"
                            v-model="includeInteractions"
                        >
                            {{ __('back-office-genesys-data-type-interactions') }}
                        </CheckboxInput>
                        <CheckboxInput
                            id="genesys-sync-data-type-email"
                            v-model="includeEmailInteractions"
                        >
                            {{ __('back-office-genesys-data-type-email-interactions') }}
                        </CheckboxInput>
                    </div>
                </FormGroup>

                <div class="mt-6">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canSubmit"
                        :loading="isPending"
                    >
                        {{ __('back-office-sync-jobs-trigger') }}
                    </EchoButton>
                </div>
            </form>

            <SyncJobsTable
                ref="syncJobsTable"
                class="mt-10"
                :heading="__('back-office-sync-jobs-recent')"
                :use-jobs-query="() => useQueryGetIntegrationSyncJobs(GENESYS_SYSTEM_KEY)"
            />
        </template>
    </SettingsPageLayout>
</template>
